import React, {useState, useEffect} from 'react';
import './Maps.css';
import { Missouri } from './Missouri';
import { MapData } from '../../interfaces/MapData';
import ProbePopup from './ProbePopup';
import {
    CRASH_HEAT_ID,
    CRASH_HEAT_TILES,
    CRASH_POINTS_ID,
    LIVE_CRASH_ID,
    crashHeatPaint,
    crashPointPaint,
    liveCrashPaint,
} from './crashHeatLayers';
import {authHeader, titanTransformRequest, waitForIdToken} from '../../services/idToken';
import {
    API,
    PROBE_TILES,
    probePaint,
    workzonePaint,
    workzoneStripePaint,
} from '../../pages/ProbeMap/probeLayers';
import {
    IonIcon,
    IonButton,
    IonImg,
} from "@ionic/react";
import {help, pin, pinSharp, snow, videocam} from "ionicons/icons";
import {
    clearDay,
    cloudy,
    mostlyClearDay,
    partlyCloudyDay,
    mostlyCloudy,
    fog,
    fogLight,
    poweredByTomorrow,
    lightWind,
    wind,
    heavyWind,
    drizzle,
    rain,
    rainLight,
    rainHeavy,
    flurries,
    snowLight,
    snowHeavy,
    freezingDrizzle,
    freezingRain,
    freezingRainLight,
    freezingRainHeavy,
    icePellets,
    icePelletsHeavy,
    icePelletsLight,
    tstorm,
} from '../../assets/weather-icons/availableWeatherIcons';

import ReactMapGl, {
    Source,
    Layer,
    NavigationControl,
    FlyToInterpolator,
    LayerProps,
    Marker,
    Popup
} from 'react-map-gl';
import {Marker as MarkerData} from '../../interfaces/Marker';
import mapboxgl from 'mapbox-gl';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -- Vite's ?worker suffix has no type declaration
import MapboxWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker?worker';
import {InteractiveMapProps} from 'react-map-gl/dist/esm/components/interactive-map';

import './Maps.css';
import {getUser} from '../../firebaseConfig';
import firebaseService from '../../services/firebaseService';
import {User} from '../../interfaces/User';
import {WeatherEvent} from '../../interfaces/WeatherEvent';
import {GeoJSON} from 'geojson';
import {WazeIncident} from '../../interfaces/WazeIncident';
import {WazeJam} from '../../interfaces/WazeJam';
import { TranscoreIncident } from '../../interfaces/TranscoreIncident';
import {
    accident,
    animal,
    block,
    closed,
    exitClosed,
    hazard,
    jam,
    noShoulder,
    other,
    pothole,
    roadwork,
    stalled
} from '../../assets/traffic-icons/availableTrafficIcons';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
mapboxgl.workerClass = MapboxWorker;

const geojson: GeoJSON.Feature = Missouri;

const trafficLayer: LayerProps = {
    id: 'traffic-layer',
    source: 'traffic',
    'source-layer': 'traffic',
    type: 'line',
    minzoom: 5,
    paint: {
        'line-width': 1.5,
        'line-color': [
            "case",
                [
                    "==",
                    "low",
                    [
                        "get",
                        "congestion"
                    ]
                ],
                "#4caf50",
                [
                    "==",
                    "moderate",
                    [
                        "get",
                        "congestion"
                    ]
                ],
                "#ff3409",
                [
                    "==",
                    "heavy",
                    [
                        "get",
                        "congestion"
                    ]
                ],
                "#fc930a",
                [
                    "==",
                    "severe",
                    [
                        "get",
                        "congestion"
                    ]
                ],
                "#e73e3a",
                "#4caf50"
        ]
    }
};

const stateLayer: LayerProps = {
    id: 'fill',
    type: 'fill',
    source: 'missouri',
    paint: {
        'fill-color': '#3880ff',
        'fill-opacity': 0.2
    }
};

const Map: React.FC<MapData> = (props: MapData) => {
    const [pins, setPins] = useState<JSX.Element[]>([]);
    const [latitude, setLatitude] = useState<number>(38.631);
    const [longitude, setLongitude] = useState<number>(-90.345);
    const [weather, setWeather] = useState<WeatherEvent[]>([]);
    const [transcoreIncidents, setTranscoreIncidents] = useState<TranscoreIncident[]>([]);
    const [wazeIncidents, setWazeIncidents] = useState<WazeIncident[]>([]);
    // const [wazeJams, setWazeJams] = useState<WazeJam[]>([]);
    const [wazeJamGeo, setWazeJamGeo] = useState<GeoJSON.FeatureCollection>();
    const accessToken = import.meta.env.REACT_APP_MAPBOX_API_KEY;
    const mapState: InteractiveMapProps = {
        latitude: latitude,
        longitude: longitude,
        zoom: 8,
        minZoom: 5,
        transitionDuration: 0,
        transitionInterpolator: null
    };

    const [viewport, setViewport] = React.useState(mapState);

    const navControlStyle = {
        right: 10,
        top: 10
    };

    const recenter = () => {
        mapState.transitionDuration = 1000;
        mapState.transitionInterpolator = new FlyToInterpolator();
        setViewport({...mapState, latitude: latitude, longitude: longitude});
    };

    // Probe tiles and work zones are served by the TITAN API behind a Firebase
    // token, so the source waits for the token cache: mapbox-gl does not retry
    // a tile that came back 401.
    const [tokenReady, setTokenReady] = React.useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [workzoneData, setWorkzoneData] = React.useState<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [liveCrashData, setLiveCrashData] = React.useState<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [feature, setFeature] = React.useState<{lng: number; lat: number; layer: string; p: any} | null>(null);

    React.useEffect(() => {
        if (!props.probe && !props.workzones && !props.liveCrashes) {
            return;
        }
        let cancelled = false;
        waitForIdToken().then(ok => !cancelled && setTokenReady(ok));
        return () => { cancelled = true; };
    }, [props.probe, props.workzones, props.liveCrashes]);

    React.useEffect(() => {
        if (!props.workzones || !tokenReady || workzoneData) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/v1/workzones.geojson`, {headers: await authHeader()});
                if (res.ok && !cancelled) {
                    setWorkzoneData(await res.json());
                }
            } catch {
                // The cameras are the point of this map; a missing overlay
                // should not take the page down with it.
            }
        })();
        return () => { cancelled = true; };
    }, [props.workzones, tokenReady, workzoneData]);

    // An open crash is the one thing here that changes minute to minute, so
    // this refreshes rather than loading once.
    React.useEffect(() => {
        if (!props.liveCrashes || !tokenReady) {
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const res = await fetch(`${API}/v1/crashes/active.geojson`, {headers: await authHeader()});
                if (res.ok && !cancelled) {
                    setLiveCrashData(await res.json());
                }
            } catch {
                // Leave whatever was last drawn rather than blanking the layer.
            }
        };
        load();
        const timer = setInterval(load, 60000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [props.liveCrashes, tokenReady]);

    // Only a layer that is drawn can be clicked.
    const probeLayerIds = [
        props.probe && tokenReady ? 'probe-speeds' : null,
        props.workzones && workzoneData ? 'workzone-line' : null,
        // The heatmap itself is not clickable: a cell is an arbitrary grid
        // square, so a popup on it would report a number about a box nobody
        // can see. The individual crashes are.
        props.crashes ? CRASH_POINTS_ID : null,
        props.liveCrashes && liveCrashData ? LIVE_CRASH_ID : null,
    ].filter(Boolean) as string[];

    const handleViewportChange = (viewport: InteractiveMapProps) => {
        if (viewport.latitude && viewport.longitude && viewport.zoom) {
            if (viewport.longitude > -88.5608) {
                viewport.longitude = -88.5608;
            }
            if (viewport.longitude < -96.2512) {
                viewport.longitude = -96.2512;
            }
            if (viewport.latitude > 40.8969) {
                viewport.latitude = 40.8969;
            }
            if (viewport.latitude < 35.5143) {
                viewport.latitude = 35.5143;
            }
            if (viewport.zoom < 5) {
                viewport.zoom = 5;
            }
        }
        setViewport(viewport);
    };

    const cameraMarkers = React.useMemo(() => props.cameras?.map(
        camera => (
            <Marker latitude={camera.latitude} longitude={camera.longitude} key={camera.id}>
                <IonIcon className="map-icon" color='primary' md={videocam} onClick={() => props.setId ? props.setId(camera.id) : {}} />
            </Marker>
        ),
      ), [props]);

    const weatherMarkers = React.useMemo(() => weather.map(weatherItem => {
        let iconType: string;
        let weatherType: string;
        switch(weatherItem.weatherCode) {
            case 0:
                iconType = help;
                weatherType = 'invalid';
                break;
            case 1000:
                iconType = clearDay;
                weatherType = 'Clear Day';
                break;
            case 1001:
                iconType = cloudy;
                weatherType = 'Cloudy';
                break;
            case 1100:
                iconType = mostlyClearDay;
                weatherType = "Mostly Clear Day";
                break;
            case 1101:
                iconType = partlyCloudyDay;
                weatherType = "Partly Cloudy Day";
                break;
            case 1102:
                iconType = mostlyCloudy;
                weatherType = "Mostly Cloudy";
                break;
            case 2000:
                iconType = fog;
                weatherType = "Foggy";
                break;
            case 2100:
                iconType = fogLight;
                weatherType = "Light Fog";
                break;
            case 3000:
                iconType = lightWind;
                weatherType = "Light Wind";
                break;
            case 3001:
                iconType = wind;
                weatherType = "Windy";
                break;
            case 3002:
                iconType = heavyWind;
                weatherType = "Heavy Wind";
                break;
            case 4000:
                iconType = drizzle;
                weatherType = "Drizzle";
                break;
            case 4001:
                iconType = rain;
                weatherType = "Rainy";
                break;
            case 4200:
                iconType = rainLight;
                weatherType = "Light Rain";
                break;
            case 4201:
                iconType = rainHeavy;
                weatherType = "Heavy Rain";
                break;
            case 5000:
                iconType = snow;
                weatherType = "Snowy";
                break;
            case 5001:
                iconType = flurries;
                weatherType = "Snow Flurries";
                break;
            case 5100:
                iconType = snowLight;
                weatherType = "Light Snow";
                break;
            case 5101:
                iconType = snowHeavy;
                weatherType = "Heavy Snow";
                break;
            case 6000:
                iconType = freezingDrizzle;
                weatherType = "Freezing Drizzle";
                break;
            case 6001:
                iconType = freezingRain;
                weatherType = "Freezing Rain";
                break;
            case 6200:
                iconType = freezingRainLight;
                weatherType = "Light Freezing Rain";
                break;
            case 6201:
                iconType = freezingRainHeavy;
                weatherType = "Heavy Freezing Rain";
                break;
            case 7000:
                iconType = icePellets;
                weatherType = "Hail";
                break;
            case 7101:
                iconType = icePelletsHeavy;
                weatherType = "Heavy Hail";
                break;
            case 7102:
                iconType = icePelletsLight;
                weatherType = "Light Hail";
                break;
            case 8000:
                iconType = tstorm;
                weatherType = "Thunderstorm";
                break;
            default:
                iconType = help;
                break;
        }
            return (
                <Marker latitude={weatherItem.latitude} longitude={weatherItem.longitude} key={weatherItem.id}>
                    <IonIcon onClick={() => {
                        if (props.markerSelection) {
                            props.markerSelection(new MarkerData(
                                'Weather Event',
                                weatherItem.county,
                                weatherItem.latitude,
                                weatherItem.longitude,
                                new Date(weatherItem.timestamp),
                                weatherType,
                                '',
                                weatherItem.temperature,
                                weatherItem.windGust,
                                weatherItem.precipitationIntensity,
                                weatherItem.snowIntensity,
                                weatherItem.freezingRangeIntensity,
                                weatherItem.sleetIntensity
                                ));
                        }
                    }
                    } className="marker-icon" src={iconType} />
                </Marker>
            );
        },
    ), [weather, props]);

    const transcoreMarkers = React.useMemo(() => transcoreIncidents.map(incident => {
        let iconType;
        let iconColor;

        switch (incident.event_class) {
            case "ROADWORK":
                iconType = roadwork;
                iconColor = "yellow";
                break;
            case "OTHER":
                iconType = other;
                iconColor = "black";
                break;
            case "EXIT CLOSED":
                iconType = exitClosed;
                iconColor = "danger";
                break;
            case "STALLED VEHICLE":
                iconType = stalled;
                iconColor = "warning";
                break;
            default:
                iconType = pin;
                break;
        }
        return <Marker
            key={incident.uuid}
            longitude={incident.longitude}
            latitude={incident.latitude}>
            <IonIcon onClick={() => {
                if (props.markerSelection) {
                    props.markerSelection(new MarkerData(
                        incident.event_class,
                        incident.county,
                        incident.latitude,
                        incident.longitude,
                        new Date(incident.pub_millis),
                        incident.event_description,
                        incident.on_street_name
                    ));
                }
            }
            } className="marker-icon" color={iconColor} src={iconType} />
        </Marker>
    }), [transcoreIncidents, props]);

    const wazeIncidentMarkers = React.useMemo(() => wazeIncidents.map(incident => {
        let iconType;
        let iconColor;
        if (incident.event_class === '') {
            incident.event_class = incident.type;
        }
        switch (incident.event_class) {
            case "ROAD_CLOSED":
                iconType = closed;
                iconColor = "red";
                break;
            case "ROADWORK":
                iconType = roadwork;
                iconColor = "yellow";
                break;
            case "":
                iconType = pin;
                break;
            case "STALLED VEHICLE_ON_ROAD":
                iconType = stalled;
                iconColor = "warning";
                break;
            case "STALLED VEHICLE_SHOULDER":
                iconType = stalled;
                iconColor = "warning";
                break;
            case "JAM_HEAVY_TRAFFIC":
                iconType = jam;
                iconColor = "red";
                break;
            case "POT_HOLE":
                iconType = pothole;
                iconColor = "orange";
                break;
            case "ROAD_OBJECT":
                iconType = block;
                iconColor = "danger";
                break;
            case "JAM_MODERATE_TRAFFIC":
                iconType = jam;
                iconColor = "red";
                break;
            case "ACCIDENT_MINOR":
                iconType = accident;
                iconColor = "warning";
                break;
            case "ROAD_KILL_ROAD":
                iconType = animal;
                iconColor = "purple";
                break;
            case "JAM_STAND_STILL_TRAFFIC":
                iconType = jam;
                iconColor = "red";
                break;
            case "ACCIDENT_MAJOR":
                iconType = accident;
                iconColor = "danger";
                break;
            case "ACCIDENT":
                iconType = accident;
                iconColor = "orange";
                break;
            case "HAZARD_ON_SHOULDER":
                iconType = hazard;
                iconColor = "danger";
                break;
            case "SHOULDER_MISSING_SIGN":
                iconType = noShoulder;
                iconColor = "warning";
                break;
            default:
                iconType = pin;
                break;
        }
        return <Marker
            key={incident.uuid}
            longitude={incident.longitude}
            latitude={incident.latitude}>
            <IonIcon onClick={() => {
                if (props.markerSelection) {
                    props.markerSelection(new MarkerData(
                        incident.event_class,
                        incident.county,
                        incident.latitude,
                        incident.longitude,
                        new Date(incident.pub_millis),
                        incident.report_description,
                        incident.street
                    ));
                }
            }
            } className="marker-icon" color={iconColor} src={iconType}/>
        </Marker>
    }), [wazeIncidents, props]);

    function updateLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                getUser()?.then(user => {
                    const data = user.data();
                    if (data && data.allowTracking !== false) {
                        firebaseService.updateUser(new User(
                            data.uid,
                            data.admin,
                            data.applied,
                            data.verified,
                            data.deviceIDs,
                            data.email,
                            data.displayName,
                            data.agency,
                            position.coords.latitude,
                            position.coords.longitude,
                            true,
                            data.subscriptions
                        )).catch(err => {
                            console.error('Failed to update user: ' + err);
                        });
                    }
                });
            });
        }
    }

    useEffect(() => {
        getUser()?.then(user => {
            const userData = user.data();
            if (userData && userData.allowTracking) {
                if (userData.latitude && userData.longitude) {
                    setPins([
                        <Marker latitude={userData.latitude} longitude={userData.longitude} key={1}>
                            <IonIcon size="large" color='pink' md={pinSharp} />
                        </Marker>
                    ]);
                    setLatitude(userData.latitude);
                    setLongitude(userData.longitude);
                    setViewport(v => ({...v, latitude: userData.latitude, longitude: userData.longitude}));
                }
                updateLocation();
            } else {
                setPins([]);
            }
        });
        firebaseService.watchWazeIncidentsData().onSnapshot((wazeData) => {
            const data = wazeData.docs;
            const foundIncidents: WazeIncident[] = [];
            data.forEach(doc => {
                const docData = doc.data();
                if (docData != null) {
                    foundIncidents.push(docData as WazeIncident);
                }
            });
            setWazeIncidents(foundIncidents);
        });
        firebaseService.watchWazeJamsData().onSnapshot((wazeData) => {
            const data = wazeData.docs;
            // const foundJams: WazeJam[] = [];
            const wazeFeatures: GeoJSON.Feature[] = [];
            data.forEach(doc => {
                const docData = doc.data() as WazeJam;
                if (docData != null) {
                    let speedColor;
                    const speed = docData.speed;

                    if (speed < 1) {
                        speedColor = 'red';
                    } else if (speed <= 5) {
                        speedColor = 'orangered';
                    } else if (speed <= 10) {
                        speedColor = 'gold';
                    } else if (speed <= 15) {
                        speedColor = 'greenyellow';
                    } else if (speed <= 20) {
                        speedColor = 'palegreen';
                    } else if (speed <= 25) {
                        speedColor = 'mediumseagreen';
                    } else {
                        speedColor = 'forestgreen';
                    }
                    wazeFeatures.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: [
                                [docData.start_long, docData.start_lat],
                                [docData.end_long, docData.end_lat]
                            ]
                        },
                        properties: {
                            color: speedColor
                        }
                    });
                }
            });
            setWazeJamGeo({
                type: 'FeatureCollection',
                features: wazeFeatures
            });
        });
        firebaseService.watchTranscoreData().onSnapshot((transcoreData) => {
            const data = transcoreData.docs;
            const foundIncidents: TranscoreIncident[] = [];
            data.forEach(doc => {
                const docData = doc.data() as TranscoreIncident;
                if (docData != null) {
                    foundIncidents.push(docData);
                }
            });
            setTranscoreIncidents(foundIncidents);
        });
        firebaseService.watchWeatherData().onSnapshot((weatherData) => {
            const data = weatherData.docs;
            const foundWeather: WeatherEvent[] = [];
            data.forEach((doc) => {
                const docData = doc.data();
                if (docData != null) {
                    foundWeather.push(new WeatherEvent(
                        doc.id,
                        docData.county,
                        docData.pub_millis,
                        docData.weatherCode,
                        docData.latitude,
                        docData.longitude,
                        docData.temperature,
                        docData.rainIntensity,
                        docData.windGust,
                        docData.snowIntensity,
                        docData.freezingRainIntensity,
                        docData.sleetIntensity,
                
                        ));
                }
            });
            setWeather(foundWeather);
        });
    }, []);

    // Without a token mapbox-gl never initialises, so onLoad never fires and
    // anything waiting on it (the CCTV page's spinner) waits forever.
    useEffect(() => {
        if (!accessToken && props.setShowLoading) {
            props.setShowLoading(false);
        }
    }, [accessToken, props]);

    if (!accessToken) {
        return (
            <div className="map-unavailable" style={{height: props.height}}>
                <p className="map-unavailable__title">Map unavailable</p>
                <p className="map-unavailable__detail">
                    REACT_APP_MAPBOX_API_KEY is not set, so the base map cannot load.
                </p>
            </div>
        );
    }

    return(
        <div className="map-root">
            <ReactMapGl
                {...viewport}
                onViewportChange={handleViewportChange}
                mapStyle="mapbox://styles/mapbox/dark-v10"
                mapboxApiAccessToken={accessToken}
                width="100%"
                height={props.height}
                transformRequest={titanTransformRequest}
                interactiveLayerIds={probeLayerIds}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick={(event: any) => {
                    const hit = event.features?.[0];
                    const [lng, lat] = event.lngLat;
                    setFeature(hit ? {lng, lat, layer: hit.layer.id, p: hit.properties ?? {}} : null);
                }}
                onLoad={() => props.setShowLoading ? props.setShowLoading(false) : {}}
            >
                {props.cctv && cameraMarkers}
                {props.location && pins}
                {props.weather && weatherMarkers}
                {props.transcore && transcoreMarkers}
                {props.wazeIncidents && wazeIncidentMarkers}
                {props.traffic &&
                    <Source id='traffic' type='vector' url='mapbox://mapbox.mapbox-traffic-v1' minzoom={5}>
                        <Layer {...trafficLayer} />
                    </Source>}
                {props.stateOutline &&
                    <Source id='missouri' type='geojson' data={geojson}>
                        <Layer {...stateLayer} />
                    </Source>}
                {props.wazeJams &&
                    <Source id='wazeJam' type='geojson' data={wazeJamGeo}>
                        <Layer
                            id="wazeJamLayer"
                            type="line"
                            source="wazeJam"
                            paint={
                                {
                                    'line-color': [
                                        'match',
                                        ['get', 'color'],
                                        'red',
                                        'red',
                                        'orangered',
                                        'orangered',
                                        'gold',
                                        'gold',
                                        'greenyellow',
                                        'greenyellow',
                                        'palegreen',
                                        'palegreen',
                                        'mediumseagreen',
                                        'mediumseagreen',
                                        'forestgreen',
                                        'forestgreen',
                                        'red'
                                    ]
                                }
                            }
                        />
                    </Source>}
                {/* Work zones sit under the probe speeds, so a slow segment on
                    a work-zone road stays readable. */}
                {props.workzones && workzoneData &&
                    <Source id='workzones' type='geojson' data={workzoneData}>
                        <Layer id="workzone-line" type="line" paint={workzonePaint}
                               layout={{'line-cap': 'round', 'line-join': 'round'}} />
                        <Layer id="workzone-stripes" type="line" paint={workzoneStripePaint}
                               layout={{'line-cap': 'butt', 'line-join': 'miter'}} />
                    </Source>}
                {/* Under everything else: five years of history should not
                    cover what the road is doing right now. minzoom 4 because a
                    single band-0 tile already covers the state; maxzoom 16
                    because past that the features are individual crashes and
                    mapbox can overzoom them. */}
                {props.crashes &&
                    <Source id='crash-heat-src' type='vector' tiles={[CRASH_HEAT_TILES]}
                            minzoom={4} maxzoom={16}>
                        <Layer id={CRASH_HEAT_ID} type="heatmap" source-layer="crash_heat"
                               maxzoom={16} paint={crashHeatPaint} />
                        <Layer id={CRASH_POINTS_ID} type="circle" source-layer="crash_heat"
                               minzoom={13.5} filter={['has', 'crash_id']} paint={crashPointPaint} />
                    </Source>}
                {props.probe && tokenReady &&
                    <Source id='probe' type='vector' tiles={[PROBE_TILES]} minzoom={6} maxzoom={16}>
                        <Layer id="probe-speeds" type="line" source-layer="probe_layer" paint={probePaint} />
                    </Source>}
                {/* Above the history and the road layers: an open crash is the
                    thing you would act on. */}
                {props.liveCrashes && liveCrashData &&
                    <Source id='crash-live-src' type='geojson' data={liveCrashData}>
                        <Layer id={LIVE_CRASH_ID} type="circle" paint={liveCrashPaint} />
                    </Source>}
                {feature &&
                    <Popup longitude={feature.lng} latitude={feature.lat}
                           onClose={() => setFeature(null)} closeOnClick={false} captureScroll={true}>
                        <ProbePopup layer={feature.layer} p={feature.p} />
                    </Popup>}
                <NavigationControl style={navControlStyle} />
                <div className="recenter-container">
                    <IonButton className="recenter" color="light" onClick={recenter}>Recenter</IonButton>
                    {props.weather && <IonImg className="tomorrow-logo" src={poweredByTomorrow} />}
                </div>
            </ReactMapGl>
        </div>
    );
};

export default Map;
